export const SENSITIVE_FIELD_NAMES = [
  'authorization',
  'cookie',
  'set-cookie',
  'password',
  'token',
  'email',
  'phone',
  'address',
  'query',
  'search-query',
  'q',
  'coordinate',
  'coordinates',
  'latitude',
  'longitude',
  'lat',
  'lon',
  'lng',
  'minlat',
  'maxlat',
  'minlon',
  'maxlon',
  'bbox',
  'geometry',
  'gpx',
] as const;

export const REDACTED_VALUE = '[REDACTED]';
export const MAX_STRING_LENGTH = 300;
export const TRUNCATED_SUFFIX = '...[TRUNCATED]';

type QueryStringTuple = readonly [unknown, unknown];

export type SanitizableQueryString =
  string | readonly QueryStringTuple[] | Record<string, unknown> | null | undefined;

export type SanitizableSentryEvent = {
  type?: string;
  message?: string;
  logentry?: {
    message?: string;
    params?: unknown[];
  };
  exception?: {
    values?: Array<{
      value?: string;
      [key: string]: unknown;
    }>;
  };
  request?: {
    headers?: Record<string, string | undefined>;
    url?: string;
    data?: unknown;
    query_string?: SanitizableQueryString;
  };
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown> | undefined>;
  breadcrumbs?: SanitizableSentryBreadcrumb[];
  tags?: Record<string, unknown>;
  user?: Record<string, unknown>;
  spans?: SanitizableSentrySpan[];
};

export type SanitizableSentryBreadcrumb = {
  type?: string;
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
};

type SanitizableSentrySpan = {
  description?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
};

const SENSITIVE_FIELD_LOOKUP = new Set<string>(SENSITIVE_FIELD_NAMES);
const URL_PARSE_BASE = 'http://localhost';
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const BEARER_TOKEN_PATTERN = /\bBearer\s+[A-Z0-9._~+/=-]+/gi;
const JWT_PATTERN = /\beyJ[A-Z0-9_-]+\.[A-Z0-9_-]+\.[A-Z0-9_-]+\b/gi;
const COORDINATE_PAIR_PATTERN = /-?\d{1,3}\.\d{3,}\s*[,;]\s*-?\d{1,3}\.\d{3,}/g;

export function sanitizeSentryEvent<T>(event: T): T {
  const sanitizedEvent: SanitizableSentryEvent = {
    ...(event as SanitizableSentryEvent),
  };

  if (typeof sanitizedEvent.message === 'string') {
    sanitizedEvent.message = sanitizeString(sanitizedEvent.message);
  }

  if (sanitizedEvent.logentry && isRecord(sanitizedEvent.logentry)) {
    sanitizedEvent.logentry = {
      ...sanitizedEvent.logentry,
      message:
        typeof sanitizedEvent.logentry.message === 'string'
          ? sanitizeString(sanitizedEvent.logentry.message)
          : sanitizedEvent.logentry.message,
      params: Array.isArray(sanitizedEvent.logentry.params)
        ? sanitizedEvent.logentry.params.map(sanitizeUnknownValue)
        : sanitizedEvent.logentry.params,
    };
  }

  if (sanitizedEvent.exception?.values) {
    sanitizedEvent.exception = {
      ...sanitizedEvent.exception,
      values: sanitizedEvent.exception.values.map((exception) => ({
        ...exception,
        value:
          typeof exception.value === 'string' ? sanitizeString(exception.value) : exception.value,
      })),
    };
  }

  if (sanitizedEvent.request && isRecord(sanitizedEvent.request)) {
    sanitizedEvent.request = sanitizeRequest(sanitizedEvent.request);
  }

  if (sanitizedEvent.extra && isRecord(sanitizedEvent.extra)) {
    sanitizedEvent.extra = sanitizeUnknownRecord(sanitizedEvent.extra);
  }

  if (sanitizedEvent.contexts && isRecord(sanitizedEvent.contexts)) {
    sanitizedEvent.contexts = Object.entries(sanitizedEvent.contexts).reduce<
      Record<string, Record<string, unknown> | undefined>
    >((contexts, [key, value]) => {
      contexts[key] = isRecord(value) ? sanitizeUnknownRecord(value) : value;
      return contexts;
    }, {});
  }

  if (Array.isArray(sanitizedEvent.breadcrumbs)) {
    sanitizedEvent.breadcrumbs = sanitizedEvent.breadcrumbs.map(sanitizeSentryBreadcrumb);
  }

  if (sanitizedEvent.tags && isRecord(sanitizedEvent.tags)) {
    sanitizedEvent.tags = sanitizeUnknownRecord(sanitizedEvent.tags);
  }

  if (sanitizedEvent.user && isRecord(sanitizedEvent.user)) {
    sanitizedEvent.user =
      typeof sanitizedEvent.user['id'] === 'string' ? { id: sanitizedEvent.user['id'] } : {};
  }

  if (Array.isArray(sanitizedEvent.spans)) {
    sanitizedEvent.spans = sanitizedEvent.spans.map((span) => ({
      ...span,
      description:
        typeof span.description === 'string'
          ? sanitizePotentialUrl(span.description)
          : span.description,
      data: isRecord(span.data) ? sanitizeUnknownRecordWithUrls(span.data) : span.data,
    }));
  }

  return sanitizedEvent as T;
}

export function sanitizeSentryBreadcrumb<T>(breadcrumb: T): T {
  const sanitizableBreadcrumb = breadcrumb as SanitizableSentryBreadcrumb;
  return {
    ...sanitizableBreadcrumb,
    message:
      typeof sanitizableBreadcrumb.message === 'string'
        ? sanitizePotentialUrl(sanitizableBreadcrumb.message)
        : sanitizableBreadcrumb.message,
    data: isRecord(sanitizableBreadcrumb.data)
      ? sanitizeUnknownRecordWithUrls(sanitizableBreadcrumb.data)
      : sanitizableBreadcrumb.data,
  } as T;
}

function sanitizeRequest(
  request: NonNullable<SanitizableSentryEvent['request']>,
): SanitizableSentryEvent['request'] {
  if (!request) {
    return request;
  }

  return {
    ...request,
    headers: isRecord(request.headers)
      ? (sanitizeKeyValueRecord(request.headers) as Record<string, string | undefined>)
      : request.headers,
    url: typeof request.url === 'string' ? sanitizeUrl(request.url) : request.url,
    data: sanitizeUnknownValue(request.data),
    query_string: sanitizeQueryString(request.query_string),
  };
}

function sanitizeQueryString(rawQuery: SanitizableQueryString): SanitizableQueryString {
  if (rawQuery === null || rawQuery === undefined) {
    return rawQuery;
  }

  if (typeof rawQuery === 'string') {
    const params = new URLSearchParams(rawQuery);
    for (const [key, value] of params.entries()) {
      params.set(key, isSensitiveField(key) ? REDACTED_VALUE : sanitizeString(value));
    }
    return params.toString();
  }

  if (Array.isArray(rawQuery)) {
    return rawQuery.map((item) => {
      if (!Array.isArray(item) || item.length !== 2) {
        return item;
      }
      const key = String(item[0]);
      const value = String(item[1]);
      return isSensitiveField(key) ? [key, REDACTED_VALUE] : [key, sanitizeString(value)];
    });
  }

  if (isRecord(rawQuery)) {
    return sanitizeUnknownRecord(rawQuery);
  }

  return rawQuery;
}

function sanitizeUrl(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl, URL_PARSE_BASE);

    parsed.searchParams.forEach((value, key) => {
      parsed.searchParams.set(key, isSensitiveField(key) ? REDACTED_VALUE : sanitizeString(value));
    });

    return sanitizeString(parsed.toString());
  } catch {
    return sanitizeString(rawUrl);
  }
}

function sanitizeKeyValueRecord(input: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(input).reduce<Record<string, unknown>>((acc, [key, value]) => {
    acc[key] = isSensitiveField(key) ? REDACTED_VALUE : sanitizeUnknownValue(value);
    return acc;
  }, {});
}

function sanitizeUnknownRecord(input: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(input).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (isSensitiveField(key)) {
      acc[key] = REDACTED_VALUE;
      return acc;
    }

    acc[key] =
      key.trim().toLowerCase() === 'url' && typeof value === 'string'
        ? sanitizeUrl(value)
        : sanitizeUnknownValue(value);
    return acc;
  }, {});
}

function sanitizeUnknownRecordWithUrls(input: Record<string, unknown>): Record<string, unknown> {
  const sanitized = sanitizeUnknownRecord(input);
  for (const [key, value] of Object.entries(sanitized)) {
    if (key.trim().toLowerCase() === 'url' && typeof value === 'string') {
      sanitized[key] = sanitizeUrl(value);
    }
  }
  return sanitized;
}

function sanitizeUnknownValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeUnknownValue(item));
  }

  if (isRecord(value)) {
    return sanitizeUnknownRecord(value);
  }

  return String(value);
}

function isSensitiveField(key: string): boolean {
  const normalizedKey = key.trim().toLowerCase();
  if (SENSITIVE_FIELD_LOOKUP.has(normalizedKey)) {
    return true;
  }

  const compactKey = normalizedKey.replace(/[^a-z0-9]/g, '');
  return SENSITIVE_FIELD_NAMES.some((field) => {
    const compactField = field.replace(/[^a-z0-9]/g, '');
    return compactField.length >= 5 && compactKey.includes(compactField);
  });
}

function sanitizePotentialUrl(value: string): string {
  if (/^(?:https?:\/\/|\/)/i.test(value)) {
    return sanitizeUrl(value);
  }

  return sanitizeString(value);
}

function sanitizeString(value: string): string {
  const redacted = /<gpx[\s>]/i.test(value)
    ? REDACTED_VALUE
    : value
        .replace(EMAIL_PATTERN, REDACTED_VALUE)
        .replace(BEARER_TOKEN_PATTERN, REDACTED_VALUE)
        .replace(JWT_PATTERN, REDACTED_VALUE)
        .replace(COORDINATE_PAIR_PATTERN, REDACTED_VALUE);

  return redacted.length > MAX_STRING_LENGTH
    ? `${redacted.slice(0, MAX_STRING_LENGTH)}${TRUNCATED_SUFFIX}`
    : redacted;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
